// Shared dashboard-grid geometry — the ONE placement math for every surface
// that renders a dashboard tile grid (designer layout grid, in-app preview,
// smart-page embeds, deployed runtime client).
//
// Tiles are positioned in 12-column × row units (POSITION.X/Y/WIDTH/HEIGHT),
// but coordinates may be fractional in quarter-unit steps — the dashboard
// designer resizes/moves at 0.25-unit granularity. CSS grid placement needs
// integer tracks, so every surface renders a sub-grid of DASH_SUB tracks per
// unit with the inter-tile gap carried as a per-tile margin. That keeps
// legacy integer models at their exact previous pixel geometry:
//   tile width  = units × (colW + gap) − gap   (same as a 12-col grid w/ gap)
//   tile height = units × (rowPx + gap) − gap
import type { CSSProperties } from 'react'

export const DASH_COLS = 12
// CSS grid tracks per layout unit → quarter-unit (0.25) granularity.
export const DASH_SUB = 4
// Finest position/size increment, in layout units.
export const DASH_STEP = 1 / DASH_SUB

export interface DashTilePosition {
  X?: number
  Y?: number
  WIDTH?: number
  HEIGHT?: number
}

// Snap a unit coordinate onto the quarter-unit sub-grid.
export function dashSnap(v: number): number {
  return Math.round(v * DASH_SUB) / DASH_SUB
}

function num(v: unknown, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

// Grid container style. `rowPx`/`gapPx`/`padPx` describe the surface's
// legacy 12-col geometry (row height, inter-tile gap, container padding);
// the sub-grid reproduces it with gap 0 + per-tile margin of gapPx/2
// (see dashTileStyle) and padding reduced by that margin.
// `stretch` (default true) lets rows grow to fill a fixed-height container.
export function dashGridContainerStyle(
  rowPx: number,
  gapPx: number,
  padPx: number,
  opts?: { stretch?: boolean },
): CSSProperties {
  const track = (rowPx + gapPx) / DASH_SUB
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${DASH_COLS * DASH_SUB}, 1fr)`,
    gridAutoRows: opts?.stretch === false
      ? `${track}px`
      : `minmax(${track}px, 1fr)`,
    gap: 0,
    padding: Math.max(0, padPx - gapPx / 2),
  }
}

// Placement + margin for one tile (the margin restores the inter-tile gap).
// Defensive against missing/garbage positions and clamps into the 12 cols.
export function dashTileStyle(
  pos: DashTilePosition | undefined | null,
  gapPx: number,
): CSSProperties {
  const p = pos || {}
  const x = Math.max(0, num(p.X, 0))
  const y = Math.max(0, num(p.Y, 0))
  const w = Math.max(DASH_STEP, num(p.WIDTH, 6))
  const h = Math.max(DASH_STEP, num(p.HEIGHT, 4))
  const maxTracks = DASH_COLS * DASH_SUB
  const colStart = Math.min(maxTracks - 1, Math.round(x * DASH_SUB)) + 1
  const colSpan = Math.max(1, Math.min(maxTracks - colStart + 1, Math.round(w * DASH_SUB)))
  const rowStart = Math.round(y * DASH_SUB) + 1
  const rowSpan = Math.max(1, Math.round(h * DASH_SUB))
  return {
    gridColumn: `${colStart} / span ${colSpan}`,
    gridRow: `${rowStart} / span ${rowSpan}`,
    margin: gapPx / 2,
  }
}

// Exact pixel height of a grid whose lowest tile bottom is `maxRowUnits`
// (fractional allowed) — matches dashGridContainerStyle's geometry, so an
// auto-height embed fits its tiles exactly.
export function dashGridHeightPx(
  maxRowUnits: number,
  rowPx: number,
  gapPx: number,
  padPx: number,
): number {
  const tracks = Math.ceil(Math.max(0, maxRowUnits) * DASH_SUB)
  const track = (rowPx + gapPx) / DASH_SUB
  return tracks * track + Math.max(0, padPx - gapPx / 2) * 2
}
